import React, { useEffect } from 'react';

import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useUser } from 'global/UserContext';
import { useUserProfileImage } from 'pages/IndividualBio/hooks/useUserProfileImage';
import { Notification } from 'types/notificationsTypes';

interface NotificationTooltipProps {
  notification: Notification;
  isVisible: boolean;
  onClose: () => void;
  position?: 'header' | 'mobile';
  anchorEl?: HTMLElement | null;
}

const NotificationTooltip: React.FC<NotificationTooltipProps> = ({
  notification,
  isVisible,
  onClose,
  position = 'header',
  anchorEl,
}) => {
  const theme = useTheme();
  const { user } = useUser();
  const { imageUrl } = useUserProfileImage(
    notification.data.requester_id || notification.data.avatar_user_id
  );

  // Auto-hide após 10 segundos
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  const renderImage = () => {
    if (notification.origin.includes('meeting_request')) {
      const userAvatar =
        notification.data.requester_id === user?.id
          ? notification.data.host_id
          : notification.data.requester_id;
      const newImageUrl = imageUrl?.replace(user?.id || '', userAvatar);
      return (
        <Avatar src={newImageUrl ?? undefined} alt={userAvatar} sx={{ width: 24, height: 24 }} />
      );
    }
    return (
      <Avatar
        src={imageUrl ?? undefined}
        alt={notification.data.avatar_user_id}
        sx={{ width: 24, height: 24 }}
      />
    );
  };

  const tooltipContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        maxWidth: 280,
        minWidth: 200,
      }}
    >
      {renderImage()}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#000',
            fontSize: '0.875rem',
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {notification.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {notification.message}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            mt: 0.5,
            display: 'block',
          }}
        >
          Agora
        </Typography>
      </Box>
    </Box>
  );

  if (!isVisible || !anchorEl) return null;

  return (
    <Tooltip
      open={isVisible}
      onClose={onClose}
      title={tooltipContent}
      placement={position === 'header' ? 'bottom' : 'right'}
      arrow
      PopperProps={{
        disablePortal: false,
        style: { zIndex: 9999 },
        anchorEl: anchorEl,
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'primary.main',
            color: '#000',
            padding: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            border: '1px solid primary.main',
            maxWidth: 280,
            minWidth: 200,
            '& .MuiTooltip-arrow': {
              color: 'primary.main',
            },
          },
        },
        arrow: {
          sx: {
            color: 'primary.main',
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
    </Tooltip>
  );
};

export default NotificationTooltip;
